// Padding used for scroll positioning and button width
export const X_PADDING = 20;

export interface DragState {
  isDragging: boolean;
  startX: number;
  scrollLeft: number;
  lastMoveX: number;
  lastMoveTime: number;
  velocity: number;
}

export interface MomentumController {
  startMomentum: (
    velocity: number,
    container: HTMLElement,
    onMomentumStart?: () => void,
    onMomentumEnd?: () => void
  ) => number | null;
  cancelMomentum: (animationId: number | null) => void;
  calculateVelocity: (
    currentX: number,
    lastX: number,
    currentTime: number,
    lastTime: number
  ) => number;
  initializeDragState: (
    x: number,
    container: HTMLElement
  ) => Partial<DragState>;
  updateDragState: (
    x: number,
    dragState: DragState,
    container: HTMLElement
  ) => void;
  finalizeDrag: (container: HTMLElement) => void;
}

// Check if scroll position is beyond bounds
const getScrollBounds = (container: HTMLElement) => {
  const maxScrollLeft = container.scrollWidth - container.offsetWidth;
  return { min: 0, max: Math.max(0, maxScrollLeft) };
};

export const createMomentumController = (): MomentumController => {
  let currentAnimationId: number | null = null;
  let isAnimationCancelled = false;

  const startMomentum = (
    velocity: number,
    container: HTMLElement
  ): number | null => {
    if (!container || Math.abs(velocity) < 0.1) return null;

    // Cancel any existing animation
    if (currentAnimationId) {
      cancelAnimationFrame(currentAnimationId);
    }
    isAnimationCancelled = false;

    let currentVelocity = velocity;

    const animate = (): void => {
      if (isAnimationCancelled) {
        currentAnimationId = null;
        return;
      }

      const bounds = getScrollBounds(container);
      const newScrollLeft = container.scrollLeft - currentVelocity;

      // Clamp scroll position to bounds without any elastic effect
      if (newScrollLeft < bounds.min) {
        container.scrollLeft = bounds.min;
        currentAnimationId = null;
        return;
      } else if (newScrollLeft > bounds.max) {
        container.scrollLeft = bounds.max;
        currentAnimationId = null;
        return;
      } else {
        container.scrollLeft = newScrollLeft;
        currentVelocity *= 0.95;
      }

      // Continue animation
      if (Math.abs(currentVelocity) < 0.1) {
        currentAnimationId = null;
        return;
      }

      currentAnimationId = requestAnimationFrame(animate);
    };

    currentAnimationId = requestAnimationFrame(animate);
    return currentAnimationId;
  };

  const cancelMomentum = (): void => {
    isAnimationCancelled = true;
    if (currentAnimationId) {
      cancelAnimationFrame(currentAnimationId);
      currentAnimationId = null;
    }
  };

  const calculateVelocity = (
    currentX: number,
    lastX: number,
    currentTime: number,
    lastTime: number
  ): number => {
    const timeDiff = currentTime - lastTime;
    if (timeDiff <= 0) return 0;
    return ((currentX - lastX) / timeDiff) * 16;
  };

  const initializeDragState = (
    x: number,
    container: HTMLElement
  ): Partial<DragState> => {
    container.style.cursor = 'grabbing';
    container.style.userSelect = 'none';

    return {
      isDragging: true,
      startX: x - container.offsetLeft,
      scrollLeft: container.scrollLeft,
      lastMoveX: x,
      lastMoveTime: Date.now(),
      velocity: 0,
    };
  };

  const updateDragState = (
    x: number,
    dragState: DragState,
    container: HTMLElement
  ): void => {
    if (!dragState.isDragging) return;

    const walk = x - dragState.startX;
    const targetScrollLeft = dragState.scrollLeft - walk;
    const bounds = getScrollBounds(container);

    // Clamp scroll position to bounds without any elastic effect
    container.scrollLeft = Math.max(
      bounds.min,
      Math.min(bounds.max, targetScrollLeft)
    );

    const currentTime = Date.now();
    dragState.velocity = calculateVelocity(
      x,
      dragState.lastMoveX,
      currentTime,
      dragState.lastMoveTime
    );
    dragState.lastMoveX = x;
    dragState.lastMoveTime = currentTime;
  };

  const finalizeDrag = (container: HTMLElement): void => {
    container.style.cursor = 'grab';
    container.style.userSelect = '';
  };

  return {
    startMomentum,
    cancelMomentum,
    calculateVelocity,
    initializeDragState,
    updateDragState,
    finalizeDrag,
  };
};

export interface SlideDetectionResult {
  currentIndex: number;
  shouldUpdate: boolean;
}

export const detectCurrentSlide = (
  container: HTMLElement,
  totalSlides: number
): SlideDetectionResult => {
  const items = container.querySelectorAll('[data-slider-item]');
  if (items.length === 0) {
    return { currentIndex: 0, shouldUpdate: false };
  }

  const containerScrollLeft = container.scrollLeft;
  const containerWidth = container.offsetWidth;
  const maxScrollLeft = container.scrollWidth - containerWidth;

  // Edge case: Only at absolute beginning (truly at x=0)
  if (containerScrollLeft === 0) {
    return { currentIndex: 0, shouldUpdate: true };
  }

  // Edge case: At or very close to the absolute end (add tolerance for floating point precision)
  if (containerScrollLeft >= maxScrollLeft - 1) {
    const lastIndex = totalSlides - 1;
    return { currentIndex: lastIndex, shouldUpdate: true };
  }

  // Find current item: switch when item is more than 40% out of view AND next item is at least 40% visible
  let selectedIndex = 0;

  items.forEach((item) => {
    const element = item as HTMLElement;
    const itemIndex = parseInt(element.getAttribute('data-index') || '0');
    const itemLeft = element.offsetLeft;
    const itemWidth = element.offsetWidth;

    // Calculate how much of the item is out of the left side of the viewport
    const leftOut = containerScrollLeft - itemLeft;
    // Calculate how much of the item is out of the right side of the viewport
    const rightOut =
      itemLeft + itemWidth - (containerScrollLeft + containerWidth);

    // Calculate how much of this item is visible
    const visibleWidth = Math.max(
      0,
      itemWidth - Math.max(0, leftOut) - Math.max(0, rightOut)
    );
    const visibilityPercentage = visibleWidth / itemWidth;

    // If more than 40% of the item is out of the left, consider next item
    if (leftOut > 0.4 * itemWidth) {
      // Don't switch if current item is 100% visible
      if (visibilityPercentage >= 1.0) {
        selectedIndex = itemIndex;
      } else {
        // Check if the next item would be at least 50% visible before switching
        const nextItem = items[itemIndex + 1] as HTMLElement;
        if (nextItem) {
          const nextItemLeft = nextItem.offsetLeft;
          const nextItemWidth = nextItem.offsetWidth;
          const nextItemLeftOut = containerScrollLeft - nextItemLeft;
          const nextItemRightOut =
            nextItemLeft +
            nextItemWidth -
            (containerScrollLeft + containerWidth);
          const nextItemVisibleWidth = Math.max(
            0,
            nextItemWidth -
              Math.max(0, nextItemLeftOut) -
              Math.max(0, nextItemRightOut)
          );
          const nextItemVisibilityPercentage =
            nextItemVisibleWidth / nextItemWidth;

          if (nextItemVisibilityPercentage >= 0.5) {
            selectedIndex = itemIndex + 1;
          } else {
            selectedIndex = itemIndex;
          }
        } else {
          selectedIndex = itemIndex;
        }
      }
    }
  });

  return { currentIndex: selectedIndex, shouldUpdate: true };
};

export const scrollToSlide = (container: HTMLElement, index: number): void => {
  const items = container.querySelectorAll('[data-slider-item]');
  const item = items[index] as HTMLElement;
  if (!item) return;

  const targetScrollLeft = item.offsetLeft - X_PADDING;

  // Use browser's native smooth scrollTo
  container.scrollTo({
    left: Math.max(0, targetScrollLeft),
    behavior: 'smooth',
  });
};

export interface DragHandlers {
  handlePointerStart: (x: number) => void;
  handlePointerMove: (x: number) => void;
  handlePointerEnd: () => void;
}

export const createDragHandlers = (
  containerRef: React.RefObject<HTMLDivElement | null>,
  dragState: React.MutableRefObject<DragState>,
  momentumController: React.MutableRefObject<MomentumController>,
  momentumRef: React.MutableRefObject<number | null>,
  dragStarted: React.MutableRefObject<boolean>
): DragHandlers => {
  const handlePointerStart = (x: number) => {
    const container = containerRef.current;
    if (!container || !momentumController.current || !dragState.current) return;

    // Cancel any existing momentum scrolling
    momentumController.current.cancelMomentum(momentumRef.current);
    momentumRef.current = null;

    const newState = momentumController.current.initializeDragState(
      x,
      container
    );
    Object.assign(dragState.current, newState);

    // Reset drag started flag
    dragStarted.current = false;
  };

  const handlePointerMove = (x: number) => {
    const container = containerRef.current;
    if (!container || !momentumController.current || !dragState.current) return;

    const state = dragState.current;
    momentumController.current.updateDragState(x, state, container);

    // If we've moved enough to consider it a drag, mark it as started
    if (state.isDragging && Math.abs(x - state.startX) > 5) {
      dragStarted.current = true;
    }
  };

  const handlePointerEnd = () => {
    const state = dragState.current;
    if (!state || !state.isDragging || !momentumController.current) return;

    const container = containerRef.current;
    if (!container) return;

    state.isDragging = false;
    momentumController.current.finalizeDrag(container);

    // Start momentum scrolling
    momentumRef.current = momentumController.current.startMomentum(
      state.velocity,
      container
    );
  };

  return {
    handlePointerStart,
    handlePointerMove,
    handlePointerEnd,
  };
};

export interface NavigationHandlers {
  handlePrevious: () => void;
  handleNext: () => void;
}

export const createNavigationHandlers = (
  currentSlide: number,
  totalSlides: number,
  scrollToSlide: (index: number) => void,
  momentumController: React.MutableRefObject<MomentumController>,
  momentumRef: React.MutableRefObject<number | null>
): NavigationHandlers => {
  const handlePrevious = () => {
    if (currentSlide > 0 && momentumController.current) {
      // Cancel any existing momentum scrolling before transitioning
      momentumController.current.cancelMomentum(momentumRef.current);
      momentumRef.current = null;
      scrollToSlide(currentSlide - 1);
    }
  };

  const handleNext = () => {
    if (currentSlide < totalSlides - 1 && momentumController.current) {
      // Cancel any existing momentum scrolling before transitioning
      momentumController.current.cancelMomentum(momentumRef.current);
      momentumRef.current = null;
      scrollToSlide(currentSlide + 1);
    }
  };

  return {
    handlePrevious,
    handleNext,
  };
};

export interface GlobalEventHandlers {
  handleGlobalMouseMove: (e: MouseEvent) => void;
  handleGlobalMouseUp: (e: MouseEvent) => void;
  handleGlobalClick: (e: MouseEvent) => void;
  handleMouseDownCapture: () => void;
  cleanup: () => void;
}

export const createGlobalEventHandlers = (
  handlePointerMove: (x: number) => void,
  handlePointerEnd: () => void,
  dragStarted: React.MutableRefObject<boolean>,
  momentumController: React.MutableRefObject<MomentumController>,
  momentumRef: React.MutableRefObject<number | null>
): GlobalEventHandlers => {
  const handleGlobalMouseMove = (e: MouseEvent) => {
    e.preventDefault();
    handlePointerMove(e.pageX);
  };

  const handleGlobalMouseUp = (e: MouseEvent) => {
    // If a drag started, prevent any click events on links
    if (dragStarted.current) {
      e.preventDefault();
      e.stopPropagation();
    }

    handlePointerEnd();
    document.removeEventListener('mousemove', handleGlobalMouseMove);
    document.removeEventListener('mouseup', handleGlobalMouseUp);
  };

  const handleGlobalClick = (e: MouseEvent) => {
    // If a drag started, prevent the click event
    if (dragStarted.current) {
      e.preventDefault();
      e.stopPropagation();
      dragStarted.current = false; // Reset after preventing click
    }
  };

  const handleMouseDownCapture = () => {
    document.addEventListener('mousemove', handleGlobalMouseMove);
    document.addEventListener('mouseup', handleGlobalMouseUp);
    document.addEventListener('click', handleGlobalClick, true);
  };

  const cleanup = () => {
    document.removeEventListener('mousemove', handleGlobalMouseMove);
    document.removeEventListener('mouseup', handleGlobalMouseUp);
    document.removeEventListener('click', handleGlobalClick, true);
    if (momentumController.current) {
      momentumController.current.cancelMomentum(momentumRef.current);
    }
  };

  return {
    handleGlobalMouseMove,
    handleGlobalMouseUp,
    handleGlobalClick,
    handleMouseDownCapture,
    cleanup,
  };
};
